(() => {
  const tagName = 'find-your-vehicle-faster';
  if (customElements.get(tagName)) return;

  class FindYourVehicleFaster extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.cleanup = [];

      if (!this.tabs.length || !this.panels.length) return;

      this.tabs.forEach((tab, index) => {
        this.listen(tab, 'click', () => this.activate(index, { focus: false, scroll: true }));
        this.listen(tab, 'keydown', (event) => this.onKeydown(event, index));
      });

      this.onBlockSelect = (event) => {
        const blockId = event.detail?.blockId;
        if (!blockId || !this.contains(event.target)) return;

        const index = this.panels.findIndex((panel) => panel.dataset.blockId === blockId);
        if (index >= 0) this.activate(index, { focus: false, scroll: true });
      };

      document.addEventListener('shopify:block:select', this.onBlockSelect);

      const requestedIndex = Number.parseInt(this.dataset.initialIndex, 10) - 1;
      const currentIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      const initialIndex = Number.isFinite(requestedIndex) ? requestedIndex : currentIndex;
      this.activate(Math.max(0, Math.min(initialIndex, this.tabs.length - 1)), {
        focus: false,
        scroll: true
      });
    }

    disconnectedCallback() {
      this.cleanup?.forEach((remove) => remove());
      this.cleanup = [];
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.initialized = false;
    }

    listen(target, eventName, handler) {
      target.addEventListener(eventName, handler);
      this.cleanup.push(() => target.removeEventListener(eventName, handler));
    }

    onKeydown(event, currentIndex) {
      const lastIndex = this.tabs.length - 1;
      let nextIndex = currentIndex;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = lastIndex;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activate(nextIndex, { focus: true, scroll: true });
    }

    activate(index, { focus = false, scroll = false } = {}) {
      const activeIndex = Math.max(0, Math.min(index, this.tabs.length - 1));

      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === activeIndex;
        tab.setAttribute('aria-selected', String(active));
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.panels.forEach((panel, panelIndex) => {
        const active = panelIndex === activeIndex;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));

        if ('inert' in panel) panel.inert = !active;
      });

      const activeTab = this.tabs[activeIndex];
      if (focus) activeTab.focus({ preventScroll: true });

      if (scroll) {
        window.requestAnimationFrame(() => this.revealTab(activeTab));
      }

      this.dispatchEvent(
        new CustomEvent('vehicle-finder:change', {
          bubbles: true,
          detail: { index: activeIndex, blockId: this.panels[activeIndex]?.dataset.blockId }
        })
      );
    }

    revealTab(tab) {
      const tabList = tab.closest('[role="tablist"]');
      if (!tabList || tabList.scrollWidth <= tabList.clientWidth) return;

      const listRect = tabList.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const visibleStart = tabList.scrollLeft;
      const visibleEnd = visibleStart + tabList.clientWidth;
      const tabStart = visibleStart + tabRect.left - listRect.left;
      const tabEnd = tabStart + tabRect.width;
      const maxScroll = tabList.scrollWidth - tabList.clientWidth;
      let target = visibleStart;

      if (tabStart < visibleStart) {
        target = tabStart;
      } else if (tabEnd > visibleEnd) {
        target = tabEnd - tabList.clientWidth;
      }

      tabList.scrollTo({ left: Math.max(0, Math.min(target, maxScroll)), behavior: 'smooth' });
    }
  }

  customElements.define(tagName, FindYourVehicleFaster);
})();
